import React, { useState, useEffect, useRef } from 'react';
import countries from '../utils/countryData';

const CountryAutocomplete = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const wrapperRef = useRef(null);

  // Initialize with value if provided
  useEffect(() => {
    if (value) {
      const country = countries.find(c => c.name === value);
      if (country) {
        setSelectedCountry(country);
        setInputValue(country.name);
      }
    }
  }, [value]);

  // Filter suggestions based on input
  const filterSuggestions = (input) => {
    const filtered = countries.filter(country => 
      country.name.toLowerCase().includes(input.toLowerCase())
    );
    setSuggestions(filtered.slice(0, 10)); // Limit to 10 suggestions
    setActiveSuggestionIndex(0);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const input = e.target.value;
    setInputValue(input);
    
    if (input.length > 0) {
      filterSuggestions(input);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    
    // Clear selected country if input doesn't match
    if (selectedCountry && selectedCountry.name !== input) {
      setSelectedCountry(null);
      onChange('');
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (country) => {
    setSelectedCountry(country);
    setInputValue(country.name);
    setSuggestions([]);
    setShowSuggestions(false);
    onChange(country.name);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Enter key
    if (e.keyCode === 13) {
      e.preventDefault();
      if (suggestions.length > 0 && showSuggestions) {
        handleSuggestionClick(suggestions[activeSuggestionIndex]);
      }
    }
    // Up arrow
    else if (e.keyCode === 38) {
      if (activeSuggestionIndex > 0) {
        setActiveSuggestionIndex(activeSuggestionIndex - 1);
      }
    }
    // Down arrow
    else if (e.keyCode === 40) {
      if (activeSuggestionIndex < suggestions.length - 1) {
        setActiveSuggestionIndex(activeSuggestionIndex + 1);
      }
    }
    // Escape key
    else if (e.keyCode === 27) {
      setShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus input when clicking on the wrapper
  const handleWrapperClick = () => {
    const inputElement = wrapperRef.current.querySelector('input');
    if (inputElement) {
      inputElement.focus();
    }
  };

  return (
    <div className="country-autocomplete" ref={wrapperRef}>
      <div className="country-input-wrapper" onClick={handleWrapperClick}>
        <input
          type="text"
          className="form-input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue && setShowSuggestions(true)}
          placeholder="Start typing a country name..."
        />
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="country-suggestions">
          {suggestions.map((country, index) => (
            <div
              key={country.name}
              className={`country-suggestion ${index === activeSuggestionIndex ? 'active' : ''}`}
              onClick={() => handleSuggestionClick(country)}
            >
              <span className="country-flag">{country.flag}</span>
              <span>{country.name}</span>
            </div>
          ))}
        </div>
      )}
      
      {showSuggestions && suggestions.length === 0 && inputValue && (
        <div className="country-suggestions">
          <div className="no-suggestions">No countries found</div>
        </div>
      )}
    </div>
  );
};

export default CountryAutocomplete; 